<script setup>
import dayjs from 'dayjs'
import { ref, computed } from 'vue'
import { formatDayMonthDate } from '@/lib/utils'

const props = defineProps({
  service: {
    type: Object,
    required: true,
  },
  projectStart: {
    type: [String, Date],
    required: true,
  },
  projectEnd: {
    type: [String, Date],
    required: true,
  },
  // Optional custom (hex) colour for the range — used to match the project colour
  // once the service allocation is confirmed. Falls back to the primary palette.
  color: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update-range'])

const startOfProject = computed(() => dayjs(props.projectStart).startOf('day'))
const dayCount = computed(() => Math.max(0, dayjs(props.projectEnd).startOf('day').diff(startOfProject.value, 'day')))

const toOffset = (value, fallback) => {
  if (!value) return fallback
  const offset = dayjs(value).startOf('day').diff(startOfProject.value, 'day')
  return Math.min(Math.max(offset, 0), dayCount.value)
}

// Re-initialised from props whenever the parent re-keys this component after a refresh.
const range = ref({
  min: toOffset(props.service.start, 0),
  max: toOffset(props.service.end, dayCount.value),
})

const offsetLabel = (offset) => formatDayMonthDate(startOfProject.value.add(offset, 'day'))

const onChange = (value) => {
  const start = startOfProject.value
    .add(value.min, 'day')
    .hour(dayjs(props.projectStart).hour())
    .minute(dayjs(props.projectStart).minute())
    .second(0)
  const end = startOfProject.value
    .add(value.max, 'day')
    .hour(dayjs(props.projectEnd).hour())
    .minute(dayjs(props.projectEnd).minute())
    .second(0)
  emit('update-range', { start: start.toISOString(), end: end.toISOString() })
}
</script>

<template>
  <div v-if="dayCount > 0" class="q-pt-sm">
    <q-range
      :model-value="range"
      :min="0"
      :max="dayCount"
      :step="1"
      snap
      :color="color ? undefined : 'primary'"
      :class="{ 'range-custom-color': color }"
      :style="color ? { '--range-color': color } : undefined"
      @update:model-value="range = $event"
      @change="onChange"
    />
    <div class="row justify-between text-caption text-grey">
      <span>{{ offsetLabel(range.min) }}</span>
      <span>{{ offsetLabel(range.max) }}</span>
    </div>
  </div>
</template>

<style scoped>
.range-custom-color :deep(.q-slider__selection) {
  background: var(--range-color) !important;
}
.range-custom-color :deep(.q-slider__thumb) {
  color: var(--range-color) !important;
}
</style>
